<template>
  <div class="flex w-full flex-col">
    <div class="my-12 flex justify-center">
      <div v-if="gameData && gameData.settings" class="flex flex-col px-4">
        <div
          class="mx-auto mb-1 text-xs font-bold uppercase tracking-wider text-white"
        >
          - Game code -
        </div>
        <div class="mx-auto text-xl uppercase tracking-wider text-white">
          {{ gameData.gameId }}
        </div>
      </div>

      <div v-if="gameData.settings" class="flex flex-col px-4">
        <div
          class="mx-auto mb-1 text-xs font-bold uppercase tracking-wider text-white"
        >
          - Win Condition -
        </div>
        <div class="mx-auto text-xl uppercase tracking-wider text-white">
          {{ gameData.settings.winCondition }}
        </div>
      </div>
    </div>

    <div
      v-if="gameData && gameData._id"
      ref="wrapper"
      class="z-10 mx-auto flex h-full w-full flex-col flex-wrap items-start justify-center text-white"
      :style="wrapperStyles"
    >
      <!-- Players -->
      <div class="mx-auto mb-4">
        <div
          v-for="player in gameData.players"
          :key="player.socketId"
          class="mb-1 flex items-center px-2"
        >
          <div
            class="flex items-center px-2 py-1"
            :class="[
              {
                'rounded bg-black bg-opacity-20':
                  player.playerNumber === gameData.activePlayer,
              },
              { 'opacity-50': player.playerNumber !== gameData.activePlayer },
            ]"
          >
            <div
              class="flex h-6 w-6 select-none items-center justify-center overflow-hidden rounded-full p-1 text-xs"
              :class="[
                { 'bg-red-400': player.playerNumber === 1 },
                { 'bg-blue-400': player.playerNumber === 2 },
                { 'bg-green-400': player.playerNumber === 3 },
                { 'bg-yellow-400': player.playerNumber === 4 },
                { 'bg-purple-400': player.playerNumber === 5 },
              ]"
            ></div>
            <div class="ml-2">{{ player.username }}</div>
            <div
              v-if="gameData.settings.winCondition === 'Highest Score'"
              class="ml-4 rounded-full bg-white px-3 text-xs uppercase tracking-wide text-gray-900"
            >
              {{ player.score }}
            </div>
          </div>

          <div
            v-if="socketIds.includes(player.socketId)"
            class="ml-auto px-3 text-xs font-semibold uppercase tracking-wide text-green-400"
          >
            Connected
          </div>
          <div
            v-if="!socketIds.includes(player.socketId)"
            class="ml-auto px-3 text-xs font-semibold uppercase tracking-wide text-red-400"
          >
            Disconnected
          </div>
        </div>
      </div>

      <!-- Board -->
      <div
        class="board"
        :style="boardStyles"
        ref="board"
        :key="JSON.stringify(gameData.boardState)"
      >
        <template v-for="(row, rowIndex) in gameData.boardState">
          <div
            v-for="(slot, colIndex) in row"
            :key="'row' + rowIndex + 'col' + colIndex"
            :ref="'row' + rowIndex + 'col' + colIndex"
            class="tile transition-all duration-300"
            :class="{
              'opacity-50 saturate-0 filter':
                gameData.winner.length >= 1 &&
                gameData.winner.includes(
                  gameData.boardState[rowIndex][colIndex]
                ),
            }"
            @click="squareSelected(rowIndex, colIndex)"
          >
            <div
              class="flex h-full select-none items-center justify-center overflow-hidden rounded"
              :class="[
                { 'bg-red-400': gameData.boardState[rowIndex][colIndex] === 1 },
                {
                  'bg-blue-400': gameData.boardState[rowIndex][colIndex] === 2,
                },
                {
                  'bg-green-400': gameData.boardState[rowIndex][colIndex] === 3,
                },
                {
                  'bg-yellow-300':
                    gameData.boardState[rowIndex][colIndex] === 4,
                },
                {
                  'bg-purple-400':
                    gameData.boardState[rowIndex][colIndex] === 5,
                },
              ]"
            >
              <IconX
                v-if="gameData.boardState[rowIndex][colIndex] === 1"
                colour="#fff"
              />
              <IconCircle
                v-if="gameData.boardState[rowIndex][colIndex] === 2"
                colour="#fff"
              />
              <IconSquare
                v-if="gameData.boardState[rowIndex][colIndex] === 3"
                colour="#fff"
              />
              <IconTriangle
                v-if="gameData.boardState[rowIndex][colIndex] === 4"
                colour="#fff"
              />
              <IconStar
                v-if="gameData.boardState[rowIndex][colIndex] === 5"
                colour="#fff"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import IconCircle from '@/components/IconCircle'
import IconStar from '@/components/IconStar'
import IconSquare from '@/components/IconSquare'
import IconTriangle from '@/components/IconTriangle'
import IconX from '@/components/IconX'
import axios from 'axios'
import { socket } from '../main'

export default {
  components: {
    IconCircle,
    IconStar,
    IconSquare,
    IconTriangle,
    IconX,
  },

  props: ['propData', 'gameId', 'userId'],

  data() {
    return {
      scale: 1,
      isActiveWinScreen: false,
      gameData: {},
      socketIds: [],
    }
  },

  computed: {
    isActivePlayer() {
      const player = this.gameData.players.find(
        (player) => player.userId === this.userId
      )
      return player.playerNumber === this.gameData.activePlayer
    },

    wrapperStyles() {
      return `
        max-width: ${80 * this.gameData.settings.gridSize + (8 * this.gameData.settings.gridSize - 1)}px;
      `
    },

    boardStyles() {
      return `
        grid-template-columns: repeat(${this.gameData.settings.gridSize}, 1fr);
        grid-template-rows: repeat(${this.gameData.settings.gridSize}, 1fr);
        font-size: ${this.scale}em;
      `
    },
  },

  watch: {
    propData: {
      handler(newData, oldData) {
        if (JSON.stringify(newData) !== JSON.stringify(oldData)) {
          this.gameData = newData
        }
      },
      immediate: true,
      deep: true,
    },
  },

  created() {
    socket.emit('getSocketUsers', this.gameData.gameId)

    setInterval(() => {
      socket.emit('getSocketUsers', this.gameData.gameId)
    }, 5000)

    socket.on('socketUsers', (socketIds) => {
      this.socketIds = socketIds
    })
  },

  methods: {
    squareSelected(row, col) {
      if (!this.gameData.isActive) {
        return
      }

      if (this.gameData.status === 'complete') {
        return
      }

      if (!this.isActivePlayer) {
        return
      }

      if (this.gameData.boardState[row][col] !== 0) {
        return
      }

      this.gameData.boardState[row][col] = this.gameData.activePlayer
      this.round++

      if (!this.gameData.winner.length) {
        this.gameData.activePlayer++

        if (this.gameData.activePlayer > this.gameData.players.length) {
          this.gameData.activePlayer = 1
        }
      }

      this.$emit('playTurn', {
        gameId: this.gameId,
        boardState: this.gameData.boardState,
        activePlayer: this.gameData.activePlayer,
        round: this.round,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$tilesize: 5em;
$tilegap: 0.5em;

.tile {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0.25em;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  cursor: grab;
  position: relative;
}

.board {
  aspect-ratio: 1/1;
  display: grid;
  grid-column-gap: $tilegap;
  grid-row-gap: $tilegap;
  position: relative;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/VBoardMultiplayer.vue